/**
 * AttachmentViewer — handle-based in-chat attachment viewer (#639, Task 5.1,
 * spec §6.1).
 *
 * Renders a clicked `AttachmentChip` (Task 5.2) as a modal. Three attachment
 * shapes, three render strategies — chosen by which field is present, in
 * this priority order:
 *
 *   1. `url` (a `data:` URL already in the message part) — pure client
 *      render, NO network fetch. Covers every inline attachment, whether or
 *      not it also carries a `handle` (session.ts:2388's comment notes a
 *      part can carry both; `url` wins because the bytes are already local).
 *   2. `handle` (no `url`) — a binary attachment. Fetches
 *      `GET /api/agent/sessions/:sessionID/attachments/:handle` (Task 2.5),
 *      session-gated and `Cache-Control: private, no-store`. This viewer
 *      NEVER caches the bytes itself either: it fetches fresh on every
 *      mount and revokes its object URL on unmount, so a revoked
 *      session/handle stops resolving the next time this is opened, not
 *      just on a hard refresh.
 *   3. `path` (an insert-path drive reference, no `url`/`handle`) — this
 *      viewer does NOT re-implement a live drive-content fetch; it defers to
 *      the existing open-in-drive callback (`onOpenFile`), i.e. "keep
 *      today's behavior for this case" (spec §6.1). In the normal chip flow
 *      (Task 5.2) this branch is not reached — a path-only chip keeps
 *      calling `onOpen(path)` directly instead of opening this viewer — but
 *      it is kept here so a future caller that already has an
 *      `AttachmentViewer` open (e.g. paging through several attachments)
 *      doesn't need special-case plumbing for the drive-reference kind.
 *
 * Strict render allowlist — `isSafeInlineContentType` from `@papan/types`,
 * the same function the attachment routes use rather than a copy of the
 * same rule (it was a local copy here, justified by the server's version
 * being a private route-file helper; it is a package export now):
 * image / PDF / plain-text render inline; anything scriptable or unknown is
 * offered as a download instead of being rendered as active content. The
 * server's `Content-Disposition: attachment` header only affects a direct
 * navigation — a `fetch()` + `.blob()` read like this one gets the bytes
 * regardless of that header, so the client must apply the same rule itself
 * rather than relying on the response headers to protect it.
 */
import { useEffect, useState } from 'react'
import { Download, File as FileIcon, AlertCircle, Loader2, HardDriveUpload } from 'lucide-react'
import { Modal } from '@papan/core-ui/ui'
import { isSafeInlineContentType, damagedMessageFrom } from '@papan/types'
import { fetchSessionAttachment, sessionAttachmentUrl } from './sessionAttachmentsApi.js'

/** The subset of an `Attachment` (hooks.ts) / file `MessagePart` this viewer
 *  needs. Structural, not imported from either — both shapes already match
 *  it, and this keeps the viewer decoupled from which one a caller has. */
export interface ViewableAttachment {
  filename: string
  mime: string
  url?: string
  handle?: string
  path?: string
}

export interface AttachmentViewerProps {
  attachment: ViewableAttachment
  /** Required to build the session-gated fetch URL for a `handle` attachment. */
  sessionId: string
  /** Required alongside `sessionId` — the endpoint's IDOR gate matches on
   *  both (features/agent/api/routes/attachments.ts). */
  workspaceId: string
  onClose: () => void
  /** Drive-open callback for the `path`-only (insert-path) case — see the
   *  header note on why this viewer defers to it rather than re-fetching
   *  drive content itself. */
  onOpenFile?: (path: string) => void
  /** When provided (a session-private `handle` attachment the caller can move),
   *  render a visible "Move to drive…" action. This is the discoverable,
   *  touch-reachable path to move-to-drive — the chip's right-click menu is
   *  not (no context menu on touch, and invisible until you know it's there). */
  onSaveToDrive?: () => void
}

/** Decode a `data:` URL's payload as text for the escaped-text render path.
 *  Returns null if it isn't a parseable data URL (never throws into render). */
function decodeDataUrlText(dataUrl: string): string | null {
  const match = /^data:([^;,]*)(;base64)?,(.*)$/s.exec(dataUrl)
  if (!match) return null
  const [, , isBase64, payload] = match
  try {
    return isBase64 ? atob(payload) : decodeURIComponent(payload)
  } catch {
    return null
  }
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  // `text` is populated only for a `text/plain` response — read eagerly so
  // the render path can put it straight in a `<pre>` (React-escaped, never
  // through a document/iframe context) instead of leaning on an iframe's
  // text/plain rendering, which is correct-by-default in every browser but
  // is a platform behavior, not something this component controls.
  | { kind: 'ready'; objectUrl: string; contentType: string; text?: string }
  /** `damaged`: #1397 — the server's sentence when the stored copy is damaged (the generic line would say "removed, or
   *  no access", both wrong then). */
  | { kind: 'error'; damaged?: string }

function ViewerBody({
  attachment, sessionId, workspaceId, onOpenFile, onSaveToDrive,
}: Pick<AttachmentViewerProps, 'attachment' | 'sessionId' | 'workspaceId' | 'onOpenFile' | 'onSaveToDrive'>) {
  const hasInlineUrl = !!attachment.url
  const isHandleFetch = !hasInlineUrl && !!attachment.handle
  const [state, setState] = useState<FetchState>(isHandleFetch ? { kind: 'loading' } : { kind: 'idle' })

  useEffect(() => {
    if (!isHandleFetch || !attachment.handle) return
    let cancelled = false
    let objectUrl: string | null = null
    setState({ kind: 'loading' })

    fetchSessionAttachment(sessionId, workspaceId, attachment.handle)
      .then(async (res) => {
        // #1397: a refusal's body is read for its damage sentence (a 409 CONTENT_DAMAGED); anything else stays generic
        if (!res.ok) throw Object.assign(new Error(`status ${res.status}`), { body: await res.json().catch(() => null) })
        const blob = await res.blob()
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        const contentType = res.headers.get('content-type') || attachment.mime
        const bare = contentType.split(';')[0]?.trim().toLowerCase() ?? ''
        const text = bare === 'text/plain' ? await blob.text().catch(() => undefined) : undefined
        if (cancelled) return
        setState({ kind: 'ready', objectUrl, contentType, text })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const damaged = damagedMessageFrom((err as { body?: unknown } | null)?.body)
        setState(damaged ? { kind: 'error', damaged } : { kind: 'error' })
      })

    // Every fetch is a fresh, live-gated round trip (Task 2.5's session-
    // ownership check + `Cache-Control: private, no-store`) — nothing here
    // may persist past this viewer instance. The object URL is revoked on
    // unmount / re-fetch so a closed viewer holds no bytes in memory and a
    // reopened one re-proves access rather than reusing a stale grant.
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [isHandleFetch, sessionId, workspaceId, attachment.handle, attachment.mime])

  // ── 1. Inline — bytes are already local, render straight from the data URL. ──
  if (hasInlineUrl) {
    return <RenderedContent url={attachment.url!} mime={attachment.mime} filename={attachment.filename} onSaveToDrive={onSaveToDrive} />
  }

  // ── 2. Binary handle — fetched above. ──
  if (isHandleFetch) {
    if (state.kind === 'loading' || state.kind === 'idle') {
      return (
        <div className="flex items-center justify-center gap-2 py-10 themed-text-muted text-sm">
          <Loader2 size={16} className="animate-spin" />
          Loading attachment…
        </div>
      )
    }
    if (state.kind === 'error') {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-10 themed-text-muted text-sm">
          <AlertCircle size={20} />
          {state.damaged ?? 'Attachment unavailable — it may have been removed, or you no longer have access.'}
        </div>
      )
    }
    return <RenderedContent url={state.objectUrl} mime={state.contentType} filename={attachment.filename} text={state.text} onSaveToDrive={onSaveToDrive} />
  }

  // ── 3. Insert-path drive reference — defer to the existing open path. ──
  if (attachment.path) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-sm">
        <FileIcon size={20} className="themed-text-muted" />
        <span className="themed-text-muted">{attachment.path}</span>
        {onOpenFile && (
          <button
            type="button"
            onClick={() => onOpenFile(attachment.path!)}
            className="px-3 py-1.5 rounded themed-btn-secondary text-xs"
          >
            Open in Drive
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center gap-2 py-10 themed-text-muted text-sm">
      <AlertCircle size={16} />
      Nothing to preview.
    </div>
  )
}

/** The action row for the currently-rendered bytes: Download always, and
 *  Save-to-drive beside it when the caller can save (a session-private handle).
 *  Both live together, centered, directly under the content — never a separate
 *  bordered footer. Download works for both a `data:` URL (inline) and a
 *  `blob:` object URL (fetched handle) — the main app is not the
 *  download-blocked artifact sandbox, so `<a download>` saves the file. */
function Actions({ url, filename, onSaveToDrive }: { url: string; filename: string; onSaveToDrive?: () => void }) {
  return (
    <div className="flex items-center justify-center gap-2">
      <a
        href={url}
        download={filename}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded themed-btn-secondary text-xs"
      >
        <Download size={12} />
        Download
      </a>
      {onSaveToDrive && (
        <button
          type="button"
          onClick={onSaveToDrive}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded themed-btn-secondary text-xs"
        >
          <HardDriveUpload size={12} />
          Move to drive…
        </button>
      )}
    </div>
  )
}

/** Strict render allowlist: image / PDF / escaped plain text render inline;
 *  everything else (scriptable or unrecognized) is offered as a download —
 *  never rendered as active content. Every branch also shows the Download /
 *  Save-to-drive action row. */
function RenderedContent({ url, mime, filename, text, onSaveToDrive }: { url: string; mime: string; filename: string; text?: string; onSaveToDrive?: () => void }) {
  const bare = mime.split(';')[0]?.trim().toLowerCase() ?? ''
  if (!isSafeInlineContentType(mime)) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-sm">
        <FileIcon size={20} className="themed-text-muted" />
        <span className="themed-text-muted">{filename}</span>
        <span className="themed-text-muted text-xs">This file type can't be previewed here.</span>
        <Actions url={url} filename={filename} onSaveToDrive={onSaveToDrive} />
      </div>
    )
  }

  if (bare.startsWith('image/')) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-2">
        <img src={url} alt={filename} className="max-w-full max-h-[65vh] rounded object-contain" />
        <Actions url={url} filename={filename} onSaveToDrive={onSaveToDrive} />
      </div>
    )
  }

  if (bare === 'application/pdf') {
    return (
      <div className="flex flex-col gap-3">
        <iframe
          src={url}
          title={filename}
          className="w-full h-[65vh] rounded border-0"
          // Containment here is the MIME gate + the private scope, NOT an
          // iframe sandbox. This branch is reached only for `application/pdf`
          // via `isSafeInlineContentType` (scriptable MIMEs are excluded), the bytes
          // are session-private (a hostile PDF is one the viewer supplied to
          // their OWN session), and the browser renders it with its native PDF
          // viewer.
          //
          // Do NOT add `sandbox` here to "harden" it — it can't be applied
          // uniformly. `url` is EITHER a same-origin `blob:` object URL (the
          // fetched-handle path) OR a `data:` URL (an inline paste, or a
          // writer's inline drive image/PDF). A `sandbox=""` frame gets an
          // OPAQUE origin: it can load a `data:` URL but NOT a same-origin
          // `blob:` one, so a blanket sandbox would break the fetched-handle
          // PDF; and the only `sandbox` value that lets a `blob:` load,
          // `allow-same-origin`, re-grants the very origin access the sandbox
          // would be added to deny. Delivering every PDF as a `data:` URL to
          // make sandbox uniform is rejected (unbounded base64 for large
          // files).
        />
        <Actions url={url} filename={filename} onSaveToDrive={onSaveToDrive} />
      </div>
    )
  }

  // text/plain — render as escaped text: `text` (fetched-blob case, read via
  // `blob.text()` above) or decoded straight out of the `data:` URL (inline
  // case). Both land in a `<pre>` as a plain React child — never
  // `dangerouslySetInnerHTML`, never a document/iframe context — so the
  // content can't execute regardless of what it claims to be.
  const plainText = text ?? (url.startsWith('data:') ? decodeDataUrlText(url) : null)
  return (
    <div className="flex flex-col gap-3">
      <pre className="whitespace-pre-wrap break-words text-xs themed-text p-3 max-h-[65vh] overflow-auto">
        {plainText ?? '(could not decode text content)'}
      </pre>
      <Actions url={url} filename={filename} onSaveToDrive={onSaveToDrive} />
    </div>
  )
}

export function AttachmentViewer({ attachment, sessionId, workspaceId, onClose, onOpenFile, onSaveToDrive }: AttachmentViewerProps) {
  return (
    <Modal title={attachment.filename} onClose={onClose} maxWidth="max-w-2xl">
      <div className="p-3">
        <ViewerBody attachment={attachment} sessionId={sessionId} workspaceId={workspaceId} onOpenFile={onOpenFile} onSaveToDrive={onSaveToDrive} />
      </div>
    </Modal>
  )
}

// Exported for tests that want the URL-building rule without going through
// a full fetch mock.
export { sessionAttachmentUrl }
